/**
 * TimingModal - Modal for setting timing (duration in minutes) for questions and sections
 */
import React, { useState, useEffect } from "react"
import { Modal, InputNumber, Typography } from "antd"
import { ClockCircleOutlined } from "@ant-design/icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

const { Text } = Typography

interface TimingModalProps {
  /** Whether the modal is visible */
  isVisible: boolean
  /** Function to close the modal */
  onClose: () => void
  /** Callback when timing is confirmed */
  onConfirm: (minutes: number) => void
  /** Type of element (for display purposes) */
  elementType: "question" | "section"
  /** Current timing value (if any) */
  currentTiming?: number
}

/**
 * TimingModal Component
 */
const TimingModal: React.FC<TimingModalProps> = ({
  isVisible,
  onClose,
  onConfirm,
  elementType,
  currentTiming,
}) => {
  const [minutes, setMinutes] = useState<number | null>(currentTiming ?? null)

  // Reset state when modal opens
  useEffect(() => {
    if (isVisible) {
      setMinutes(currentTiming ?? null)
    }
  }, [isVisible, currentTiming])

  // Handle OK button click
  const handleOk = () => {
    if (minutes !== null && minutes > 0) {
      onConfirm(minutes)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    setMinutes(null)
    onClose()
  }

  const label =
    elementType === "question" ? "Length of question" : "Length of section"

  return (
    <Modal
      title={
        <span>
          <ClockCircleOutlined style={{ marginRight: 8 }} />
          Set Timing
        </span>
      }
      open={isVisible}
      onCancel={handleCancel}
      width={400}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2">
          <DataQButton type="secondary" onClick={handleCancel}>
            Cancel
          </DataQButton>
          <DataQButton
            type="primary"
            onClick={handleOk}
            isDisabled={minutes === null || minutes <= 0}
          >
            OK
          </DataQButton>
        </div>
      }
    >
      <div style={{ padding: "16px 0" }}>
        <Text style={{ display: "block", marginBottom: 8 }}>
          {label} (minutes):
        </Text>
        <InputNumber
          min={1}
          max={999}
          value={minutes}
          onChange={(value) => setMinutes(value)}
          placeholder="Enter minutes"
          style={{ width: "100%" }}
          autoFocus
          onPressEnter={handleOk}
        />
      </div>
    </Modal>
  )
}

export default TimingModal
